import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { SdkAlertResponse, SdkCatalogResponse, SdkObservationResponse } from '../lib/api-client';

/** SDKs area ViewModels — catalog, usage ledger (+stats), alert ledger. */

type Status = 'idle' | 'loading' | 'success' | 'error';

export function useSdkCatalog() {
  const apiClient = useApiClient();
  const [catalog, setCatalog] = useState<SdkCatalogResponse | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(() => {
    setStatus((s) => (s === 'success' ? s : 'loading'));
    apiClient
      .getSdkCatalog()
      .then((res) => {
        setCatalog(res);
        setStatus('success');
        setError(undefined);
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [apiClient]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { catalog, status, error, refresh, setCatalog };
}

export function useSdkUsage(kind?: string) {
  const apiClient = useApiClient();
  const [rows, setRows] = useState<SdkObservationResponse[]>([]);
  const [stats, setStats] = useState<Record<string, { versions: number; tenants: number; total: number }>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(() => {
    setStatus((s) => (s === 'success' ? s : 'loading'));
    apiClient
      .listSdkUsage({ ...(kind ? { kind } : {}), limit: 200 })
      .then((res) => {
        setRows(res.data);
        setStats(res.stats ?? {});
        setStatus('success');
        setError(undefined);
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [apiClient, kind]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { rows, stats, status, error, refresh };
}

export function useSdkAlerts() {
  const apiClient = useApiClient();
  const [alerts, setAlerts] = useState<SdkAlertResponse[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(() => {
    setStatus((s) => (s === 'success' ? s : 'loading'));
    apiClient
      .listSdkAlerts({ limit: 200 })
      .then((res) => {
        setAlerts(res.data);
        setStatus('success');
        setError(undefined);
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [apiClient]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { alerts, status, error, refresh };
}
